import { useState, useRef, useEffect } from "react";
import { theme, getCareConfig } from "../pages/design-system";

const API_BASE = "https://co-6008-ai-chatbot-5yr6.vercel.app";

function AssessmentCard({ assessment }) {
  const care = getCareConfig(assessment.condition.careLevel);
  return (
    <div style={{ background: theme.surface, borderRadius: theme.radius, border: `1px solid ${theme.border}`, overflow: "hidden", marginTop: "0.65rem", boxShadow: theme.shadow }}>

      {/* Header */}
      <div style={{ background: theme.bgDark, padding: "0.9rem 1.1rem" }}>
        <p style={{ color: "rgba(245,240,232,0.4)", fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 0.18rem", fontWeight: "600" }}>{assessment.condition.category}</p>
        <h3 style={{ color: "#F5F0E8", margin: 0, fontSize: "0.9rem", fontWeight: "600", fontFamily: theme.fontDisplay }}>{assessment.condition.name}</h3>
      </div>

      {/* Care badge */}
      <div style={{ padding: "0.5rem 1.1rem", background: care.bg, borderBottom: `1px solid ${care.dot}33`, display: "flex", alignItems: "center", gap: "0.38rem" }}>
        <div style={{ width: "5px", height: "5px", borderRadius: "50%", background: care.dot }} />
        <span style={{ color: care.text, fontWeight: "700", fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.07em" }}>{care.label}</span>
      </div>

      {/* Body */}
      <div style={{ padding: "0.9rem 1.1rem" }}>
        <p style={{ fontSize: "0.79rem", color: theme.textMid, lineHeight: 1.6, margin: "0 0 0.7rem" }}>{assessment.guidance.overview}</p>

        {assessment.guidance.selfCare?.length > 0 && (
          <div style={{ marginBottom: "0.65rem" }}>
            <p style={{ fontWeight: "700", fontSize: "0.65rem", color: theme.green, textTransform: "uppercase", letterSpacing: "0.07em", margin: "0 0 0.35rem" }}>Self-Care</p>
            {assessment.guidance.selfCare.map((item, i) => (
              <div key={i} style={{ display: "flex", gap: "0.38rem", marginBottom: "0.22rem" }}>
                <span style={{ color: theme.green, fontSize: "0.7rem", flexShrink: 0 }}>✓</span>
                <p style={{ fontSize: "0.75rem", color: theme.textMid, margin: 0, lineHeight: 1.5 }}>{item}</p>
              </div>
            ))}
          </div>
        )}

        {assessment.guidance.redFlags?.length > 0 && (
          <div style={{ background: theme.redLight, borderRadius: theme.radiusSm, padding: "0.65rem 0.8rem", marginBottom: "0.65rem" }}>
            <p style={{ fontWeight: "700", fontSize: "0.63rem", color: theme.red, textTransform: "uppercase", letterSpacing: "0.07em", margin: "0 0 0.3rem" }}>⚠ Seek Help If...</p>
            {assessment.guidance.redFlags.map((item, i) => (
              <p key={i} style={{ fontSize: "0.73rem", color: theme.red, margin: "0 0 0.18rem", lineHeight: 1.5 }}>• {item}</p>
            ))}
          </div>
        )}

        <a href={`/conditions/${assessment.condition.id}`}
          style={{ display: "inline-block", background: theme.clay, color: "#fff", textDecoration: "none", padding: "0.4rem 0.85rem", borderRadius: theme.radiusPill, fontSize: "0.72rem", fontWeight: "600", marginBottom: "0.65rem", transition: "background 0.15s" }}
          onMouseEnter={e => e.currentTarget.style.background = theme.clayDark}
          onMouseLeave={e => e.currentTarget.style.background = theme.clay}
        >Full condition info →</a>

        <p style={{ fontSize: "0.67rem", color: theme.textLight, lineHeight: 1.5, margin: 0, borderTop: `1px solid ${theme.border}`, paddingTop: "0.6rem", fontStyle: "italic" }}>
          {assessment.disclaimer}
        </p>
      </div>
    </div>
  );
}

export default function ChatWidget() {
  const [isOpen, setIsOpen]     = useState(false);
  const [messages, setMessages] = useState([{
    role: "assistant",
    content: "Hi there! 👋 I'm CareNav, your friendly NHS symptom checker. I'm here to help you understand your symptoms and find the right care. What's troubling you today?",
    type: "conversation",
  }]);
  const [input, setInput]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [history, setHistory]   = useState([]);
  const endRef                   = useRef(null);
  const isMobile                 = typeof window !== "undefined" && window.innerWidth < 640;

  useEffect(() => {
    const open = () => setIsOpen(true);
    window.addEventListener("openChatWidget", open);
    return () => window.removeEventListener("openChatWidget", open);
  }, []);

  useEffect(() => {
    if (isOpen) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isOpen]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput("");
    const newMsgs = [...messages, { role: "user", content: msg, type: "user" }];
    setMessages(newMsgs);
    setLoading(true);
    const newHist = [...history, { role: "user", content: msg }];
    try {
      const r = await fetch(`${API_BASE}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, history }),
      });
      const d = await r.json();
      setMessages([...newMsgs, { role: "assistant", content: d.message, type: d.type, assessment: d.assessment }]);
      setHistory([...newHist, { role: "assistant", content: d.message }]);
    } catch {
      setMessages([...newMsgs, { role: "assistant", content: "Sorry, I'm having trouble connecting. Please try again.", type: "error" }]);
    }
    setLoading(false);
  };

  const reset = () => {
    setMessages([{ role: "assistant", content: "Hi there! 👋 I'm CareNav. What symptoms are you experiencing today?", type: "conversation" }]);
    setHistory([]); setInput("");
  };

  return (
    <>
      {/* FLOATING BUTTON */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          style={{
            position: "fixed", bottom: "1.5rem", right: "1.5rem", zIndex: 1000,
            border: "none", cursor: "pointer", fontFamily: theme.fontBody,
            background: theme.clay, color: "#fff",
            boxShadow: `0 4px 16px ${theme.clay}55`,
            transition: "all 0.2s",
            ...(isMobile
              ? { width: "50px", height: "50px", borderRadius: "50%", fontSize: "1.2rem", display: "flex", alignItems: "center", justifyContent: "center" }
              : { borderRadius: theme.radiusPill, padding: "0.75rem 1.35rem", fontSize: "0.86rem", fontWeight: "600", display: "flex", alignItems: "center", gap: "0.45rem" })
          }}
          onMouseEnter={e => { e.currentTarget.style.background = theme.clayDark; e.currentTarget.style.transform = "scale(1.04)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = theme.clay; e.currentTarget.style.transform = "scale(1)"; }}
        >
          {isMobile ? "💬" : <><span>💬</span> Start Chat</>}
        </button>
      )}

      {/* CHAT WINDOW */}
      {isOpen && (
        <div style={{
          position: "fixed", zIndex: 1000,
          fontFamily: theme.fontBody,
          background: theme.bg,
          display: "flex", flexDirection: "column", overflow: "hidden",
          ...(isMobile
            ? { inset: 0 }
            : { bottom: "1.5rem", right: "1.5rem", width: "390px", height: "570px", borderRadius: theme.radiusLg, boxShadow: theme.shadowLg, border: `1px solid ${theme.border}` })
        }}>

          {/* HEADER */}
          <div style={{ background: theme.bgDark, padding: "0.95rem 1.2rem", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.55rem" }}>
              <div>
                <p style={{ color: "#F5F0E8", fontWeight: "600", fontSize: "0.85rem", margin: 0, fontFamily: theme.fontDisplay }}>CareNav</p>
                <p style={{ color: "rgba(245,240,232,0.38)", fontSize: "0.62rem", margin: 0 }}>AI symptom guidance</p>
              </div>
            </div>
            <div style={{ display: "flex", gap: "0.35rem" }}>
              {[["↺", reset], ["✕", () => setIsOpen(false)]].map(([icon, fn]) => (
                <button key={icon} onClick={fn} style={{ background: "rgba(245,240,232,0.08)", border: "none", color: "rgba(245,240,232,0.5)", borderRadius: "5px", padding: "0.28rem 0.5rem", cursor: "pointer", fontSize: icon === "↺" ? "0.8rem" : "0.9rem", fontFamily: theme.fontBody, transition: "background 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(245,240,232,0.14)"}
                  onMouseLeave={e => e.currentTarget.style.background = "rgba(245,240,232,0.08)"}
                >{icon}</button>
              ))}
            </div>
          </div>

          {/* EMERGENCY STRIP */}
          <div style={{ background: theme.redLight, padding: "0.4rem 1rem", borderBottom: `1px solid ${theme.red}33`, flexShrink: 0 }}>
            <p style={{ color: theme.red, fontSize: "0.65rem", margin: 0, fontWeight: "600" }}>
              🚨 Emergency? Call <strong>999</strong> immediately. Do not use this chat.
            </p>
          </div>

          {/* MESSAGES */}
          <div style={{ flex: 1, overflowY: "auto", padding: "0.95rem", display: "flex", flexDirection: "column", gap: "0.65rem" }}>
            {messages.map((msg, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: msg.role === "user" ? "flex-end" : "flex-start" }}>
                {msg.content && (
                  <div style={{
                    maxWidth: "86%", padding: "0.65rem 0.85rem",
                    borderRadius: msg.role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                    background: msg.role === "user" ? theme.clay : msg.type === "emergency" ? theme.redLight : theme.surface,
                    color: msg.role === "user" ? "#fff" : msg.type === "emergency" ? theme.red : theme.text,
                    fontSize: "0.855rem", lineHeight: 1.55, whiteSpace: "pre-wrap",
                    border: msg.role !== "user" ? `1px solid ${theme.border}` : "none",
                    boxShadow: theme.shadow,
                    fontWeight: msg.type === "emergency" ? "600" : "400",
                  }}>
                    {msg.content}
                  </div>
                )}
                {msg.assessment && <AssessmentCard assessment={msg.assessment} />}
              </div>
            ))}

            {loading && (
              <div style={{ display: "flex" }}>
                <div style={{ background: theme.surface, border: `1px solid ${theme.border}`, padding: "0.65rem 0.9rem", borderRadius: "14px 14px 14px 4px", display: "flex", gap: "0.28rem", alignItems: "center", boxShadow: theme.shadow }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{ width: "5px", height: "5px", borderRadius: "50%", background: theme.clay, animation: `bounce 1.2s ${i * 0.2}s infinite` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* INPUT */}
          <div style={{ padding: "0.7rem 0.95rem", borderTop: `1px solid ${theme.border}`, flexShrink: 0, background: theme.surface }}>
            <div style={{ display: "flex", gap: "0.45rem", background: theme.bg, borderRadius: theme.radius, padding: "0.38rem 0.38rem 0.38rem 0.8rem", alignItems: "flex-end", border: `1px solid ${theme.border}` }}>
              <textarea
                value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Describe your symptoms..."
                rows={1}
                style={{ flex: 1, border: "none", background: "transparent", outline: "none", resize: "none", fontFamily: theme.fontBody, fontSize: "0.855rem", color: theme.text, lineHeight: 1.5, maxHeight: "90px", overflowY: "auto" }}
              />
              <button
                onClick={send}
                disabled={!input.trim() || loading}
                style={{
                  background: input.trim() && !loading ? theme.clay : theme.border,
                  border: "none", borderRadius: theme.radiusSm,
                  color: input.trim() && !loading ? "#fff" : theme.textLight,
                  cursor: input.trim() && !loading ? "pointer" : "not-allowed",
                  padding: "0.48rem 0.7rem", fontSize: "0.78rem",
                  fontFamily: theme.fontBody, fontWeight: "600",
                  transition: "all 0.15s", flexShrink: 0,
                }}
                onMouseEnter={e => { if (input.trim() && !loading) e.currentTarget.style.background = theme.clayDark; }}
                onMouseLeave={e => { if (input.trim() && !loading) e.currentTarget.style.background = theme.clay; }}
              >Send</button>
            </div>
            <p style={{ fontSize: "0.62rem", color: theme.textLight, margin: "0.35rem 0 0", textAlign: "center" }}>Guidance only — not a medical diagnosis</p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes bounce { 0%, 60%, 100% { transform: translateY(0); } 30% { transform: translateY(-5px); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
        * { box-sizing: border-box; }
      `}</style>
    </>
  );
}